import {useParams} from 'react-router-dom'
import {useI18n} from '@/i18n'
import {useAuth} from '@/contexts/auth-context'
import {permissions} from '@/lib/permissions'
import {Card, CardContent, CardDescription, CardTitle} from '@/components/ui/card'
import {RowCardHeader} from '@/components/shared/RowCardHeader'
import {DetailGrid} from '@/components/shared/DetailGrid'
import {Stack} from '@/components/shared/Stack'
import {MutedText} from '@/components/shared/MutedText'
import {Checkbox} from '@/components/ui/checkbox'
import {Switch} from '@/components/ui/switch'
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import {EnforcementLevel} from '@/types/inbound-connector'
import {AttributeOperation} from '@/types/attribute-operation'
import {
    useInboundConnector,
    useUpdateConnectorSettings,
} from '@/hooks/use-inbound-connectors'
import {InlineSelectField} from '@/features/inbound-connectors/components/InlineSelectField'
import {InlineNumberField} from '@/features/inbound-connectors/components/InlineNumberField'
import {InlineCheckboxField} from '@/features/inbound-connectors/components/InlineCheckboxField'

export function OperationsSubPage() {
    const i18n = useI18n()
    const {id} = useParams<{id: string}>()
    const {hasPermission} = useAuth()
    const canManage = hasPermission(permissions.inboundConnectors.manage)

    const {data: connector, isLoading: connectorLoading} = useInboundConnector(id)
    const updateMutation = useUpdateConnectorSettings(connector?.id ?? '', {silent: true})

    if (connectorLoading) {
        return (
            <Card data-testid="operations-subpage-skeleton">
                <CardContent>
                    <MutedText>{i18n.common.loading}</MutedText>
                </CardContent>
            </Card>
        )
    }

    if (!connector) return null
    const settings = connector.settings
    const operations = settings.entityOperations
    const safeguards = settings.safeguards
    const validation = settings.validation

    const enforcementOptions = [
        {value: EnforcementLevel.STRICT, label: i18n.inboundConnectors.enforcementStrict},
        {value: EnforcementLevel.WARN, label: i18n.inboundConnectors.enforcementWarn},
        {value: EnforcementLevel.OFF, label: i18n.inboundConnectors.enforcementOff},
    ]

    const baseSettings = (): Parameters<typeof updateMutation.mutateAsync>[0] => ({
        safeguards: settings.safeguards,
        validation: settings.validation,
        batchSize: settings.batchSize,
        entityOperations: settings.entityOperations,
        rehirePolicy: settings.rehirePolicy,
        version: connector.version,
    })

    const updateValidation = async (next: typeof validation): Promise<void> => {
        await updateMutation.mutateAsync({
            ...baseSettings(),
            validation: next,
        })
    }

    const updateSafeguards = async (next: typeof safeguards): Promise<void> => {
        await updateMutation.mutateAsync({
            ...baseSettings(),
            safeguards: next,
        })
    }

    const updateEntityOps = async (entityType: string, op: string, enabled: boolean): Promise<void> => {
        const current = operations[entityType] ?? []
        const nextOps = enabled
            ? (current.includes(op) ? current : [...current, op])
            : current.filter(x => x !== op)
        await updateMutation.mutateAsync({
            ...baseSettings(),
            entityOperations: {...operations, [entityType]: nextOps},
        })
    }

    const updateBatchSize = async (next: number): Promise<void> => {
        await updateMutation.mutateAsync({
            ...baseSettings(),
            batchSize: next,
        })
    }

    const entityTypes = Object.keys(operations)

    return (
        <Stack gap="lg" data-testid="operations-subpage">
            {/* Entity Operations */}
            <Card data-testid="entity-operations-section" >
                <RowCardHeader>
                    <div>
                        <CardTitle data-testid="card-title-entity-operations">
                            {i18n.inboundConnectors.entityOperationsSection}
                        </CardTitle>
                        <CardDescription>{i18n.inboundConnectors.entityOperationsHelp}</CardDescription>
                    </div>
                </RowCardHeader>
                <CardContent>
                    {entityTypes.length === 0 ? (
                        <MutedText>{'—'}</MutedText>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead data-testid="col-connector-type">{i18n.inboundConnectors.connectorType}</TableHead>
                                    <TableHead data-testid="col-operation-create">{i18n.inboundConnectors.operationCreate}</TableHead>
                                    <TableHead data-testid="col-operation-update">{i18n.inboundConnectors.operationUpdate}</TableHead>
                                    <TableHead data-testid="col-operation-delete">{i18n.inboundConnectors.operationDelete}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {entityTypes.map(entityType => (
                                    // read-only
                                    <TableRow key={entityType} data-readonly>
                                        <TableCell>{entityType}</TableCell>
                                        <TableCell>
                                            {canManage ? (
                                                <Switch
                                                    checked={operations[entityType]?.includes(AttributeOperation.CREATE) ?? false}
                                                    data-testid={`switch-entity-op-create-${entityType}`}
                                                    aria-label={i18n.inboundConnectors.editEntityOperationInline}
                                                    onCheckedChange={(v) => updateEntityOps(entityType, AttributeOperation.CREATE, v)}
                                                />
                                            ) : (
                                                <Checkbox
                                                    checked={operations[entityType]?.includes(AttributeOperation.CREATE) ?? false}
                                                    disabled
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {canManage ? (
                                                <Switch
                                                    checked={operations[entityType]?.includes(AttributeOperation.UPDATE) ?? false}
                                                    data-testid={`switch-entity-op-update-${entityType}`}
                                                    aria-label={i18n.inboundConnectors.editEntityOperationInline}
                                                    onCheckedChange={(v) => updateEntityOps(entityType, AttributeOperation.UPDATE, v)}
                                                />
                                            ) : (
                                                <Checkbox
                                                    checked={operations[entityType]?.includes(AttributeOperation.UPDATE) ?? false}
                                                    disabled
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {canManage ? (
                                                <Switch
                                                    checked={operations[entityType]?.includes(AttributeOperation.DELETE) ?? false}
                                                    data-testid={`switch-entity-op-delete-${entityType}`}
                                                    aria-label={i18n.inboundConnectors.editEntityOperationInline}
                                                    onCheckedChange={(v) => updateEntityOps(entityType, AttributeOperation.DELETE, v)}
                                                />
                                            ) : (
                                                <Checkbox
                                                    checked={operations[entityType]?.includes(AttributeOperation.DELETE) ?? false}
                                                    disabled
                                                />
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* Validation */}
            <Card data-testid="validation-section" >
                <RowCardHeader>
                    <div>
                        <CardTitle data-testid="card-title-validation">
                            {i18n.inboundConnectors.validationSection}
                        </CardTitle>
                        <CardDescription>{i18n.inboundConnectors.validationHelp}</CardDescription>
                    </div>
                </RowCardHeader>
                <CardContent>
                    <DetailGrid columns={2}>
                        <InlineSelectField
                            label={i18n.inboundConnectors.attributeValidation}
                            value={validation.attributeValidation}
                            canEdit={canManage}
                            options={enforcementOptions}
                            onChange={(v) => updateValidation({...validation, attributeValidation: v as EnforcementLevel})}
                            testId="select-attribute-validation"
                            aria-label={i18n.inboundConnectors.editValidationLevelInline}
                        />
                        <InlineSelectField
                            label={i18n.inboundConnectors.relationshipSchema}
                            value={validation.relationshipSchemaEnforcement}
                            canEdit={canManage}
                            options={enforcementOptions}
                            onChange={(v) => updateValidation({...validation, relationshipSchemaEnforcement: v as EnforcementLevel})}
                            testId="select-relationship-schema"
                            aria-label={i18n.inboundConnectors.editValidationLevelInline}
                        />
                        <InlineSelectField
                            label={i18n.inboundConnectors.identifierUniqueness}
                            value={validation.identifierUniqueness}
                            canEdit={canManage}
                            options={enforcementOptions}
                            onChange={(v) => updateValidation({...validation, identifierUniqueness: v as EnforcementLevel})}
                            testId="select-identifier-uniqueness"
                            aria-label={i18n.inboundConnectors.editValidationLevelInline}
                        />
                        <InlineNumberField
                            label={i18n.inboundConnectors.batchSize}
                            value={settings.batchSize}
                            canEdit={canManage}
                            onCommit={updateBatchSize}
                            min={1}
                            testId="input-batch-size"
                            aria-label={i18n.inboundConnectors.batchSize}
                        />
                    </DetailGrid>
                </CardContent>
            </Card>

            {/* Safeguards */}
            <Card data-testid="safeguards-section" >
                <RowCardHeader>
                    <div>
                        <CardTitle data-testid="card-title-safeguards">
                            {i18n.inboundConnectors.safeguardsSection}
                        </CardTitle>
                        <CardDescription>{i18n.inboundConnectors.safeguardsHelp}</CardDescription>
                    </div>
                </RowCardHeader>
                <CardContent>
                    <DetailGrid columns={2}>
                        <InlineCheckboxField
                            label={i18n.inboundConnectors.safeguardsEnabled}
                            value={safeguards.enabled}
                            canEdit={canManage}
                            onCommit={(v) => updateSafeguards({...safeguards, enabled: v})}
                            enabledText={i18n.inboundConnectors.enabled}
                            disabledText={i18n.inboundConnectors.disabled}
                            id="sg-enabled"
                            testId="switch-safeguards-enabled"
                            aria-label={i18n.inboundConnectors.editSafeguardsValueInline}
                        />
                        <InlineNumberField
                            label={i18n.inboundConnectors.maxDeletePercent}
                            value={safeguards.maxDeletePercent}
                            canEdit={canManage}
                            onCommit={(v) => updateSafeguards({...safeguards, maxDeletePercent: v})}
                            min={0}
                            max={100}
                            suffix="%"
                            testId="input-max-delete-percent"
                            aria-label={i18n.inboundConnectors.editSafeguardsValueInline}
                        />
                        <InlineNumberField
                            label={i18n.inboundConnectors.maxDeleteAbsolute}
                            value={safeguards.maxDeleteAbsolute}
                            canEdit={canManage}
                            onCommit={(v) => updateSafeguards({...safeguards, maxDeleteAbsolute: v})}
                            min={0}
                            testId="input-max-delete-absolute"
                            aria-label={i18n.inboundConnectors.editSafeguardsValueInline}
                        />
                        <InlineNumberField
                            label={i18n.inboundConnectors.maxUpdatePercent}
                            value={safeguards.maxUpdatePercent}
                            canEdit={canManage}
                            onCommit={(v) => updateSafeguards({...safeguards, maxUpdatePercent: v})}
                            min={0}
                            max={100}
                            suffix="%"
                            testId="input-max-update-percent"
                            aria-label={i18n.inboundConnectors.editSafeguardsValueInline}
                        />
                        <InlineNumberField
                            label={i18n.inboundConnectors.maxCreatePercent}
                            value={safeguards.maxCreatePercent}
                            canEdit={canManage}
                            onCommit={(v) => updateSafeguards({...safeguards, maxCreatePercent: v})}
                            min={0}
                            suffix="%"
                            testId="input-max-create-percent"
                            aria-label={i18n.inboundConnectors.editSafeguardsValueInline}
                        />
                        <InlineCheckboxField
                            label={i18n.inboundConnectors.emptySourceProtection}
                            value={safeguards.emptySourceProtection}
                            canEdit={canManage}
                            onCommit={(v) => updateSafeguards({...safeguards, emptySourceProtection: v})}
                            enabledText={i18n.inboundConnectors.enabled}
                            disabledText={i18n.inboundConnectors.disabled}
                            id="sg-emptySourceProtection"
                            testId="switch-empty-source-protection"
                            aria-label={i18n.inboundConnectors.editSafeguardsValueInline}
                        />
                    </DetailGrid>
                </CardContent>
            </Card>
        </Stack>
    )
}
